import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
} from "react";
import styled from "styled-components";

import { TableDefinition } from "../../types/table";

import HeaderRow from "./components/header/headerRow";
import TableRow from "./components/body/tableRow";

import useClickHandlers from "./hooks/useClickHandlers";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import { TableAction } from "../../types/actions.table";
import { useVirtual } from "react-virtual";
import useScreenSize from "features/shared/hooks/useScreenSize";

interface Props<T> {
    data: T[];
    indexer: string;
    tableDefinition: TableDefinition<T>;
    onModalPreloadCallback?: (id: string) => Promise<T>; // fires before modal is open, returning initial data override
}

const Table = <T extends { [key: string]: any }>({
    data: dataFromProps,
    indexer: indexerFromProps,
    tableDefinition: tableDefinitionFromProps,
    onModalPreloadCallback,
}: Props<T>) => {
    const dispatch = useDispatch();
    const { sortedData, indexer, selectedItems } = useSelector(
        (state) => state.table
    );

    const parentRef = useRef<HTMLTableElement>(null);

    const [width] = useScreenSize();
    const rowVirtualizer = useVirtual({
        size: sortedData.length,
        parentRef,
        estimateSize: useCallback(() => {
            if (width < 1920) return 40;
            if (width < 2560) return 50;
            return 60;
        }, [width]),
        overscan: 3,
    });

    // Hook to handle clicks w/ and properly invoke sorting / selection
    const [
        handleRowClick,
        handleColumnClick,
        handleSelectAllClick,
        handleSelectNoneClick,
    ] = useClickHandlers();

    useLayoutEffect(() => {
        // have to use useLayoutEffect to prevent incomplete UI
        dispatch({
            type: TableAction.SET_TABLE_DEFINITION,
            tableDefinition: tableDefinitionFromProps,
        });
    }, [dispatch, tableDefinitionFromProps]);

    useLayoutEffect(() => {
        dispatch({
            type: TableAction.SET_DATA,
            data: dataFromProps,
            indexer: indexerFromProps,
        });
    }, [dispatch, dataFromProps, indexerFromProps]);

    useEffect(() => {
        dispatch({
            type: TableAction.SET_TABLE_HEIGHT,
            tableHeight: parentRef.current?.clientHeight,
        });
    }, [dispatch, parentRef.current?.clientHeight]);

    useEffect(() => {
        dispatch({
            type: TableAction.SET_SELECTED_ITEMS,
            selectedItems: [],
        });
    }, [dispatch]);

    const isRowSelected = useCallback(
        (row: Record<string, any>) =>
            selectedItems.map((item) => item.value).includes(row[indexer]),
        [selectedItems, indexer]
    );

    const displayedDataRows = useMemo(() => {
        return rowVirtualizer.virtualItems.map(
            ({ index: idx, start, size }) => {
                const row = sortedData[idx];
                return (
                    <TableRow
                        style={{
                            transform: `translateY(${start}px)`,
                            height: `${size}px`,
                        }}
                        key={row[indexer]}
                        row={row}
                        isSelected={isRowSelected(row)}
                        onRowClickHandler={(
                            e: React.MouseEvent<Element, MouseEvent>
                        ) => handleRowClick(e, row, idx)}
                        onModalPreloadCallback={onModalPreloadCallback}
                    />
                );
            }
        );
    }, [
        rowVirtualizer.virtualItems,
        sortedData,
        indexer,
        isRowSelected,
        onModalPreloadCallback,
        handleRowClick,
    ]);

    return (
        <Container>
            <StyledTable ref={parentRef}>
                <thead>
                    <HeaderRow
                        onSelectAllCallback={handleSelectAllClick}
                        onColumnClickHandler={handleColumnClick}
                    />
                </thead>
                <tbody
                    onClick={() => handleSelectNoneClick()}
                    style={{ height: `${rowVirtualizer.totalSize}px` }}
                >
                    {displayedDataRows}
                </tbody>
            </StyledTable>
        </Container>
    );
};

const StyledTable = styled.table`
    width: 100%;
    min-height: 100%;

    border-radius: 0.2rem;

    display: flex;
    flex-flow: column;

    position: relative;
    overflow: auto;

    & > * {
        &:first-child {
            flex: 0 0 2.5rem;
        }
        &:last-child {
            flex: 1;
        }
    }

    thead,
    tbody {
        width: 100%;
    }

    thead {
        display: grid;
        z-index: 1;
        position: sticky;
        top: 0;
    }

    tbody {
        position: relative;
    }

    th span,
    td span {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        display: block;
        user-select: none;
    }
`;

const Container = styled.div`
    margin: 1rem 2rem 2rem;

    border-radius: 0.2rem;
    box-shadow: 1px 0px 10px 1px rgba(231, 231, 231, 0.5);

    overflow: hidden;
    position: relative;

    background: white;

    // For mobile
    @media (max-width: 768px) {
        margin: 0;
        border-radius: 0;
        width: 100vw;
    }
`;

export default Table;
